// =============================================================================
// LES SEUILS DE CHIFFRE D'AFFAIRES — une seule source (KST-2411, écart R0-7)
// =============================================================================
// Un dirigeant à 400 k€ lisait trois réponses : « CA > 350 000 € » sur la landing
// de son partenaire, « profil peu éligible, nous sommes optimisés au-dessus de
// 500 000 € » dans le widget de réservation, puis le mandataire cochait « CA
// stable au-dessus de 500 000 € » au sas R0 de son audit. Deux seuils, qui
// vivaient en quatre constantes et plus de quarante textes recopiés.
//
// Les DEUX seuils sont des notions distinctes, et ils restent à leur valeur
// (rien ne change de valeur dans ce lot) :
//
//   · SEUIL_CA_ELIGIBILITE_PUBLIQUE (350 000 €) — ce que RemUp AFFICHE comme
//     minimum : les landings partenaires (« CA > 350 000 € »), leur générateur,
//     la demande de landing, le verdict « profil non éligible » du widget, le
//     kit de recommandation des partenaires ;
//   · SEUIL_CA_QUALIFICATION_R1 (500 000 €) — ce qui QUALIFIE un audit : le
//     critère CA du sas R0 (`sas-r0.ts`), le « hors cible » de la prospection
//     (`perte-justifiee.ts`), la valeur d'un RDV pour Meta (`meta-value.ts`),
//     le « profil peu éligible » du widget.
//
// Décision d'Eliott du 17/10/2026 (D-01, réponse A ; R0-7, R0-8) :
// l'aiguillage site → R0 (KST-2125, posé par la requalification automatique de
// KST-2238 — retenue le même jour à la place du bouton ; `SEUIL_CA_PREQUALIFICATION` de
// `apps/remup/src/data/rdv-config.ts`) s'ouvre le lundi 19/10/2026 au seuil
// d'éligibilité PUBLIQUE (350 000 €), pas à 400 k€. ⏸ Reste non tranché :
// l'alignement — ou non — de la qualification R0 / sas R1, qui reste à
// 500 000 €. Le jour où elle tombe, elle se pose ICI, et tous les textes suivent.
//
// Décision d'Eliott du 18/10/2026 (KST-2763) : « seuil 350k, après on peut
// accompagner en dessous, mais c'est moins intéressant ». Le site remup.fr
// AFFICHE 350 000 € partout — pages, FAQ, pré-rendus `static-content/`,
// `llms*.txt`, données structurées —, en lisant SEUIL_CA_ELIGIBILITE_PUBLIQUE
// (via `CHIFFRES.caMinimum` / `caMinimumTexte`). En dessous, le ton est fixé
// par `CHIFFRES.caMinimumEnDessous` : l'accompagnement reste possible, mais il est
// généralement moins intéressant — jamais « non éligible » sec. Le seuil de
// qualification R1, lui, n'a pas bougé.
//
// ⚠️ 400 k€ n'est pas EXPRIMABLE aujourd'hui : le CA se déclare par tranche
// (`packages/booking/src/lib/tranches-ca.ts` : < 350 k, 350-500 k, 500 k-1 M…),
// et une tranche qui chevauche le seuil ne déclenche rien. Chaque seuil doit
// être une BORNE de tranche — `seuils-ca.test.ts` le vérifie.
//
// ⚠️ Les Pages Functions de RemUp ne peuvent importer aucun package : la copie
// `apps/remup/functions/api/_seuils-ca.js` est GÉNÉRÉE par `pnpm sync:calendriers`.
// Ce fichier n'importe donc RIEN.
//
// Le balayage qui refuse les recopies : `apps/valorup-app/src/lib/__tests__/seuils-ca.test.ts`.
// =============================================================================

/** Le minimum AFFICHÉ au public (site, landings, widget) : 350 000 €. */
export const SEUIL_CA_ELIGIBILITE_PUBLIQUE = 350_000;

/** Le seuil qui QUALIFIE un audit (sas R0, hors cible, valeur Meta) : 500 000 €. */
export const SEUIL_CA_QUALIFICATION_R1 = 500_000;

/**
 * « 350 000 € » — espaces ORDINAIRES entre les milliers et avant « € » : c'est
 * l'écriture des textes que ces constantes remplacent (landings, widget), qu'un
 * changement d'espace ferait différer au caractère près.
 */
export function formaterSeuilCa(montant: number): string {
  return `${String(Math.round(montant)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} €`;
}

/** « 500k€ » — l'écriture courte de certaines landings et pages du site. */
export function formaterSeuilCaCourt(montant: number): string {
  return `${Math.round(montant / 1000)}k€`;
}

/** « 350 000 € » */
export const SEUIL_CA_ELIGIBILITE_PUBLIQUE_TEXTE = formaterSeuilCa(SEUIL_CA_ELIGIBILITE_PUBLIQUE);

/** « 500 000 € » */
export const SEUIL_CA_QUALIFICATION_R1_TEXTE = formaterSeuilCa(SEUIL_CA_QUALIFICATION_R1);
